export default {
  subnav: {
    overview: 'Overview',
    settings: 'Settings',
    billing: 'Billing',
  },
  dashboard: {
    greeting: 'Good day, {name}',
    memberSince: 'Member since {date}',
    verifyBanner: {
      title: 'Verify your email to start purchasing',
      resendBusy: 'Sending…',
      resendSent: 'Email sent',
      resendIdle: 'Resend verification email',
      sentConfirm: 'Verification email sent — check your inbox.',
      errorMsg: 'Something went wrong — please try again later.',
    },
    purchaseAccess: {
      title: 'Purchase access',
      emailVerified: 'Email verified',
      emailNotVerified: 'Email not verified',
      purchaseLimit: 'Can purchase up to €50',
      kycApproved: 'KYC approved',
      kycPending: 'KYC pending review',
      kycNotStarted: 'KYC not started',
      kycRequired: 'Required for purchases over €50',
      startKyc: 'Start KYC review →',
    },
    productPending: {
      title: 'Product pending',
      purchasedOn: 'Purchased {date}',
      revealKey: 'Reveal key',
      copy: 'Copy',
    },
    lastPurchase: {
      title: 'Last purchase',
      empty: 'No purchases yet — get your first key now!',
      browseCatalog: 'Browse catalog',
      viewAllOrders: 'View all orders →',
    },
    wallet: {
      balance: 'Wallet balance',
      held: '{amount} held · Available for purchases',
      available: 'Available for purchases',
      topUp: 'Top up',
      viewLedger: 'View ledger →',
    },
    favorites: {
      title: 'Your favorites ({count})',
      empty: 'No favorites yet.',
      browseCatalog: 'Browse catalog',
      viewAll: 'View all favorites →',
    },
  },
  settings: {
    breadcrumbHome: 'Home',
    breadcrumbAccount: 'Account',
    breadcrumbSettings: 'Settings',
    title: 'Account settings',
    subtitle: 'Manage your personal information and security preferences.',
    tabSettings: 'Settings',
    tabBilling: 'Billing',
    signOut: 'Sign out',
    sections: {
      personal: 'Personal info',
      password: 'Password',
      kyc: 'Security & KYC',
      email: 'Email preferences',
    },
    personal: {
      title: 'Personal information',
      completeForTopup: 'Fill in your name and billing address, then save to continue your top-up.',
      firstNameLabel: 'First name',
      firstNamePlaceholder: 'First name',
      lastNameLabel: 'Last name',
      lastNamePlaceholder: 'Last name',
      emailLabel: 'Email address',
      emailVerified: 'Verified',
      emailUnverified: 'Unverified',
      countryLabel: 'Country',
      birthDateLabel: 'Birth date',
      addressLabel: 'Address',
      addressPlaceholder: 'Street address',
      cityLabel: 'City',
      cityPlaceholder: 'City',
      postalCodeLabel: 'Postal code',
      postalCodePlaceholder: 'Postal code',
      phoneLabel: 'Phone',
      saveBusy: 'Saving…',
      save: 'Save changes',
      savedOk: 'Saved.',
      saveFailed: 'Could not save profile.',
    },
    password: {
      title: 'Change password',
      currentLabel: 'Current password',
      newLabel: 'New password',
      confirmLabel: 'Confirm new password',
      hint: 'Min 8 characters, must contain a letter and a digit.',
      updateBusy: 'Updating…',
      update: 'Update password',
      updatedOk: 'Password updated. Other sessions have been signed out.',
      updateFailed: 'Could not change password.',
      validationWeak: 'New password must be at least 8 characters and contain a letter and a digit.',
      validationMismatch: 'New password and confirmation do not match.',
      strengthWeak: 'Weak',
      strengthOk: 'OK',
      strengthStrong: 'Strong',
    },
    kyc: {
      title: 'KYC verification',
      notStartedTitle: 'KYC not started',
      notStartedDesc: 'Complete identity verification to lift withdrawal limits and unlock full platform features. This process typically takes less than 5 minutes.',
      pendingTitle: 'KYC under review',
      pendingDesc: 'Our compliance team is reviewing your submission. We will notify you by email once a decision is made.',
      pendingNote: 'Review in progress — you will be notified by email.',
      importantNote: 'Important: Make sure your account personal information (name, address, date of birth) matches the details on your documents before submitting.',
      idProofLabel: 'ID proof (front + back, max 2 files)',
      addressProofLabel: 'Proof of address (utility bill, bank statement)',
      submitBusy: 'Submitting…',
      submit: 'Submit KYC verification',
      uploadRequired: 'Please upload at least one ID document and one proof of address before submitting.',
      submitFailed: 'Could not request KYC.',
      reviewRequested: 'KYC review requested. Documents sent for review.',
      approvedTitle: 'KYC approved',
      approvedDesc: 'Your identity was successfully verified. Your account has full access to all features.',
      approvedOn: 'Approved on {date}',
      requestReReview: 'Request re-review',
    },
    emailPrefs: {
      title: 'Email preferences',
      body: 'Email preferences aren\'t configurable yet. Transactional emails (order receipts, key delivery, password resets) are always sent. Marketing emails are not used.',
      unsubscribe: 'To unsubscribe entirely, contact',
      supportEmail: "support{'@'}gladkey.shop",
    },
  },
  billing: {
    loading: 'Loading…',
    breadcrumbHome: 'Home',
    breadcrumbAccount: 'Account',
    breadcrumbBilling: 'Billing',
    title: 'Account billing',
    subtitle: 'Manage your wallet, view transaction history and invoices.',
    tabSettings: 'Settings',
    tabBilling: 'Billing',
    verifyBanner: {
      title: 'Please verify your email to top up your wallet',
      subtitle: 'Check your inbox for the verification link, or resend it from your',
      dashboardLink: 'dashboard',
    },
    wallet: {
      balanceLabel: 'Current balance',
      held: '{amount} held in pending orders',
      topUpButton: 'Top up wallet',
      fundsInstant: 'Funds added instantly',
      refundable: 'Refundable 14 days',
      noExpiry: 'No expiry',
    },
    purchaseHistory: {
      title: 'Purchase history',
      empty: 'No completed purchases yet.',
      colDate: 'Date',
      colProduct: 'Product',
      colPrice: 'Price',
      colOrder: 'Order #',
      colAction: 'Action',
      viewInvoice: 'View invoice',
      rangeAll: 'All',
      range30d: 'Last 30 days',
      range3m: 'Last 3 months',
      rangeYear: 'This year',
    },
    ledger: {
      title: 'Wallet ledger',
      empty: 'No wallet activity yet.',
      sortLatest: 'Latest first',
      sortOldest: 'Oldest first',
      balanceAfter: 'Balance: {amount}',
      labelTopup: 'Top-up',
      labelHold: 'Hold — order {id}',
      labelHoldGeneric: 'Order hold',
      labelPurchase: 'Purchase — order {id}',
      labelPurchaseGeneric: 'Purchase',
      labelRefund: 'Refund — order {id}',
      labelRefundGeneric: 'Refund',
      labelFee: 'Fee',
      labelAdjustment: 'Adjustment',
    },
    topup: {
      title: 'Top up your wallet',
      amountLabel: 'Amount',
      paymentMethodLabel: 'Payment method',
      methods: {
        TRUSTLY: 'Yapili',
        CARD: 'Credit / Debit card',
      },
      redirectNote: "You'll be redirected to our secure payment provider.",
      continueBusy: 'Redirecting…',
      continue: 'Continue to payment',
      cancel: 'Cancel',
      devSkip: 'Dev: skip provider',
      devBusy: 'Dev top-up…',
      minAmount: 'Minimum top-up is €1.00.',
      billingIncomplete: 'Please complete your billing info in Settings before topping up.',
      startFailed: 'Could not start top-up',
      devFailed: 'Topup failed',
      close: 'Close',
    },
  },
  twofa: {
    title: 'Two-factor authentication',
    statusEnabled: 'Enabled',
    statusDisabled: 'Disabled',
    loadFailed: 'Could not load 2FA status.',
    active: {
      label: '2FA is active',
      method: 'Method:',
      methodTotp: 'Authenticator app (TOTP)',
      methodEmail: 'Email code',
    },
    disable: {
      passwordLabel: 'Account password',
      confirm: 'Enter your account password to confirm disabling 2FA.',
      disableBusy: 'Disabling…',
      disable: 'Disable 2FA',
      cancel: 'Cancel',
      startButton: 'Disable 2FA',
      passwordRequired: 'Please enter your account password.',
      failed: 'Could not disable 2FA. Check your password.',
    },
    enable: {
      pitch: 'Add a second layer of security to your account. You will be asked for a code each time you sign in.',
      enableButton: 'Enable 2FA',
      chooseMethod: 'Choose a method',
      methodTotpTitle: 'Authenticator app (TOTP)',
      methodTotpDesc: 'Use Google Authenticator, Authy, or any TOTP app. Recommended.',
      methodEmailTitle: 'Email code',
      methodEmailDesc: 'A code will be sent to your registered email address on each login.',
      continueBusy: 'Setting up…',
      continue: 'Continue',
      cancel: 'Cancel',
      startFailed: 'Could not start 2FA setup.',
    },
    verify: {
      totpScanTitle: 'Scan the QR code with your authenticator app',
      totpQrAlt: 'TOTP QR code',
      totpManualSummary: "Can't scan? Enter the key manually",
      emailInstruction: 'A verification code has been sent to your registered email address. Enter it below to activate email 2FA.',
      codeLabelTotp: 'Enter 6-digit code from your app',
      codeLabelEmail: 'Enter code from your email',
      activateBusy: 'Verifying…',
      activate: 'Activate 2FA',
      cancel: 'Cancel',
      codeRequired: 'Please enter the verification code.',
      invalidCode: 'Invalid code. Please try again.',
    },
  },
};
